import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { en } from "./locales/en";
import { es } from "./locales/es";
import { SUPPORTED_LOCALES, type Dict, type Locale } from "./types";

const DICTS: Record<Locale, Dict> = { en, es };
const STORAGE_KEY = "mindforge.locale";

export interface LocaleContextValue {
  locale: Locale;
  setLocale: (next: Locale) => void;
  /** Translate a key, optionally interpolating placeholders. */
  t: (key: string, args?: Record<string, unknown>) => string;
}

export const LocaleContext = createContext<LocaleContextValue | null>(null);

/** Auto-detect from the browser; fall back to English if locale isn't supported. */
function detectInitialLocale(): Locale {
  if (typeof window === "undefined") return "en";
  try {
    // Explicit ?lang= override takes precedence (e.g. a localized
    // "play in Spanish" link → /play?lang=es). Persisted by the effect below.
    const param = new URLSearchParams(window.location.search)
      .get("lang")
      ?.toLowerCase();
    if (param && (SUPPORTED_LOCALES as readonly string[]).includes(param)) {
      return param as Locale;
    }
  } catch {
    // malformed URL — fall through
  }
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && (SUPPORTED_LOCALES as readonly string[]).includes(stored)) {
      return stored as Locale;
    }
  } catch {
    // localStorage unavailable — fall through to browser detection
  }
  const nav = navigator.language?.toLowerCase() ?? "en";
  if (nav.startsWith("es")) return "es";
  return "en";
}

interface LocaleProviderProps {
  children: ReactNode;
  /** Override for tests — bypasses detection. */
  initialLocale?: Locale;
}

export function LocaleProvider({ children, initialLocale }: LocaleProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(
    () => initialLocale ?? detectInitialLocale(),
  );

  // Persist the override so future visits / new tabs respect the choice.
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, locale);
      document.documentElement.lang = locale;
    } catch {
      // ignore — localStorage may be disabled in private browsing
    }
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
  }, []);

  const t = useCallback(
    (key: string, args?: Record<string, unknown>): string => {
      const dict = DICTS[locale];
      const value = dict[key] ?? DICTS.en[key];
      if (value === undefined) {
        // Missing key — fail visibly in dev, return the key in prod.
        if (import.meta.env.MODE !== "production") {
          // eslint-disable-next-line no-console
          console.warn(`[i18n] missing key: "${key}" for locale "${locale}"`);
        }
        return key;
      }
      if (typeof value === "function") return value(args ?? {});
      return value;
    },
    [locale],
  );

  const ctx = useMemo<LocaleContextValue>(
    () => ({ locale, setLocale, t }),
    [locale, setLocale, t],
  );

  return <LocaleContext.Provider value={ctx}>{children}</LocaleContext.Provider>;
}
